const FIELD_NAME_MAP: Record<string, string> = {
  name: 'Nombre del producto',
  sku: 'Código SKU',
  price: 'Precio',
  priceCents: 'Precio',
  currency: 'Moneda',
  stock: 'Disponibilidad / Stock',
  commercialRole: 'Rol comercial (para el Bot)',
  imageUrl: 'Imagen del producto',
  category: 'Categoría',
  categoryId: 'Categoría',
  keywords: 'Palabras clave',
  description: 'Descripción',
}

export interface ExtractedApiError {
  /** Mensaje general apto para notificaciones Toast */
  message: string
  /** Errores por campo individual para vincular con React Hook Form */
  fieldErrors: Record<string, string>
}

/**
 * Normaliza y traduce mensajes técnicos de Zod / Backend a explicaciones amigables en español
 */
function translateValidationMessage(rawMessage: string, fieldName: string): string {
  if (!rawMessage) return 'Valor inválido'

  if (rawMessage.includes('Invalid enum value') && fieldName === 'commercialRole') {
    return 'Rol comercial no reconocido. Selecciona uno de los roles válidos (Principal, Acompañante, etc.).'
  }

  if (rawMessage.includes('Expected') && rawMessage.includes('received')) {
    return 'El formato o tipo de dato ingresado no es válido para este campo.'
  }

  if (rawMessage.includes('Required') || rawMessage.includes('is required')) {
    return 'Este campo es obligatorio.'
  }

  return rawMessage
}

/**
 * Extrae y formatea errores detallados de validación provenientes de respuestas Axios / Backend
 * Especialmente estructurados por Zod (`details.fieldErrors` y `details.formErrors`).
 */
export function extractApiErrorMessage(
  error: any,
  fallbackMessage: string = 'Ocurrió un error al procesar la solicitud'
): ExtractedApiError {
  if (!error) {
    return { message: fallbackMessage, fieldErrors: {} }
  }

  const responseData = error.response?.data
  const fieldErrors: Record<string, string> = {}
  const errorLines: string[] = []

  if (responseData && typeof responseData === 'object') {
    const details = responseData.details

    // 1. Zod fieldErrors: { fieldName: ['error 1', 'error 2'] }
    if (details && typeof details === 'object' && details.fieldErrors) {
      const rawFieldErrors = details.fieldErrors as Record<string, string[] | string>

      for (const [field, rawMsgs] of Object.entries(rawFieldErrors)) {
        const msgs = Array.isArray(rawMsgs) ? rawMsgs : [String(rawMsgs)]
        if (msgs.length > 0 && msgs[0]) {
          const friendlyLabel = FIELD_NAME_MAP[field] || field
          const translated = translateValidationMessage(msgs[0], field)
          fieldErrors[field] = translated
          errorLines.push(`• ${friendlyLabel}: ${translated}`)
        }
      }
    }

    // 2. Zod formErrors a nivel raíz
    if (details && Array.isArray(details.formErrors) && details.formErrors.length > 0) {
      details.formErrors.forEach((fe: string) => {
        if (fe && typeof fe === 'string') {
          errorLines.push(`• ${fe}`)
        }
      })
    }

    // 3. Array de errores de express-validator: { errors: [{ msg, param/path }] }
    if (Array.isArray(responseData.errors)) {
      responseData.errors.forEach((e: any) => {
        const field = e.path || e.param || 'general'
        const msg = e.msg || 'Dato inválido'
        const friendlyLabel = FIELD_NAME_MAP[field] || field
        fieldErrors[field] = msg
        errorLines.push(`• ${friendlyLabel}: ${msg}`)
      })
    }

    // Si encontramos errores específicos por campo, construimos el mensaje consolidado
    if (errorLines.length > 0) {
      return {
        message: `Error de validación:\n${errorLines.join('\n')}`,
        fieldErrors,
      }
    }

    // 4. Mensaje directo provisto por el backend (message o error)
    const backendMessage = responseData.message || responseData.error
    if (typeof backendMessage === 'string' && backendMessage.trim()) {
      if (backendMessage.toLowerCase() === 'invalid body') {
        return {
          message: 'Los datos enviados no son válidos. Revisa los campos del formulario.',
          fieldErrors,
        }
      }
      return {
        message: backendMessage,
        fieldErrors,
      }
    }
  }

  // 5. Fallbacks estándar de Error de JavaScript o Axios
  const standardMessage = error.message || fallbackMessage
  return {
    message: standardMessage,
    fieldErrors,
  }
}
